/* ----------------- React --------------- */
import { useState } from "react";

/* ----------------- Child components --------------- */
import AccountSettingsApplet from "@memorang/ui/components/AccountSettingsApplet";
import AppSelectionDialog from "../components/dialogs/AppSelectionDialog";
import DeleteConfirmationDialog from "../components/dialogs/DeleteConfirmationDialog";
import DeleteFeedbackDialog from "../components/dialogs/DeleteFeedbackDialog";
import { SignoutDialog } from "../components/dialogs/SignOutDialog";

import { useThemeContext } from "@contexts/ThemeContext";
/* ----------------- Hooks --------------- */
import useAccountSettings, {
	type DialogType,
} from "../hooks/useAccountSettings";
import { useDelete } from "../hooks/useDelete";

import { PurchaseDialog } from "@features/dashboard/components/PurchaseDialog";
import FaqsDialog from "@features/faqs/components/FaqsDialog";
import { buildType } from "@helpers/expo-extras";
import { isWeb } from "@helpers/platform";
import { useAppStore } from "@hooks/useAppStore";
import { useGlobalStore } from "@hooks/useGlobalStore";
import { FeedbackDialogContainer } from "../../../containers/FeedbackDialogContainer";
/* ----------------- UI Libraries --------------- */

import { cacheKeys } from "@constants/cache-keys";
import { pageViewTypes } from "@constants/tracking";
import useAnalytics from "@hooks/useAnalytics";
import { useCustomNavigation } from "@hooks/useCustomNavigation";
import type { ExamDetails } from "@memorang/types";
import { useToast } from "@memorang/ui/components/Toast";
import { useQueryClient } from "@tanstack/react-query";
import { ExamsFilterDialog } from "components/ExamsFilterDialog";
import SetExamDateContainer from "components/SetExamDateContainer";
import { router, useNavigation } from "expo-router";
import { useTranslation } from "react-i18next";
import type { Apps } from "types/apps";
import LanguageDialog from "../components/dialogs/LanguageDialog";

type Props = {
	showBackButton?: boolean;
};

const AccountSettings = ({ showBackButton }: Props) => {
	const [openDialog, setOpenDialog] = useState<DialogType | undefined>();
	const {
		toggleShowDeleteConfirmationDialog,
		toggleShowDeleteFeedbackDialog,
		showDeleteConfirmationDialog,
		showDeleteFeedbackDialog,
		selectedReasons,
		feedback,
		handleSetFeedback,
		handleSendFeedback,
		checkboxStates,
		handleChangeCheckboxState,
	} = useDelete();

	const queryClient = useQueryClient();
	const exams = queryClient.getQueryData<ExamDetails[]>([cacheKeys.exams]);
	const hasMoreThanOneExam = exams && exams.length > 1;
	const showAudioPlaybackTestDialog = useGlobalStore(
		(state) => state.showAudioPlaybackTestDialog,
	);
	const selectedApp = useGlobalStore((state) => state.selectedApp);

	const { handleExamsFilterNavigation } = useCustomNavigation();
	const handleNavigateToExamsRoute = () => {
		handleExamsFilterNavigation(() => setOpenDialog("EXAMS_FILTER"));
	};
	const { t } = useTranslation();
	useAnalytics({
		type: pageViewTypes.settings,
	});
	const { isDarkMode, toggleDarkMode } = useThemeContext();
	const navigation = useNavigation<{ toggleDrawer?: () => void }>();
	const { showToast } = useToast();
	const appFont = useAppStore((store) => store.app.font);
	const toggleAudioPlaybackTestDialog = useGlobalStore(
		(state) => state.toggleAudioPlaybackTestDialog,
	);
	const showPaywall = useGlobalStore((state) => state.showPaywall);
	const setShowPaywall = useGlobalStore((state) => state.setShowPaywall);

	const handleToggleDarkMode = () => {
		toggleDarkMode();
		showToast({
			message: `${isDarkMode ? "Light" : "Dark"} mode has been enabled`,
			duration: 1000,
		});
	};
	const { getAccountSections } = useAccountSettings({
		toggleDialog: setOpenDialog,
		toggleDarkMode: handleToggleDarkMode,
		toggleShowDeleteFeedbackDialog,
		toggleAudioPlaybackTestDialog,
	});

	const handleCloseDialog = () => setOpenDialog(undefined);

	const sections = getAccountSections();

	return (
		<>
			<AccountSettingsApplet
				sections={sections}
				font={appFont}
				isDarkMode={isDarkMode}
				showAudioPlaybackTestDialog={showAudioPlaybackTestDialog}
				showPaywall={showPaywall}
				accountExtendValidity={t("account.extendValidity")}
				hasMoreThanOneExam={hasMoreThanOneExam}
				toggleDarkMode={toggleDarkMode}
				toggleAudioPlaybackTestDialog={toggleAudioPlaybackTestDialog}
				toggleShowPaywall={() => setShowPaywall(!showPaywall)}
				handleNavigateToExamsRoute={handleNavigateToExamsRoute}
				customAppBar={
					showBackButton
						? {
								title: t("account.title"),
								leftIconType: "back",
								onPressBack: () => router.back(),
							}
						: {
								title: t("account.title"),
								navigation,
							}
				}
			/>
			<SetExamDateContainer
				showDatePicker={openDialog === "DATE_PICKER"}
				toggleShowDatePicker={handleCloseDialog}
			/>
			<ExamsFilterDialog
				visible={openDialog === "EXAMS_FILTER"}
				handleClose={handleCloseDialog}
			/>
			<SignoutDialog
				visible={openDialog === "SIGN_OUT"}
				handleClose={handleCloseDialog}
			/>
			<DeleteFeedbackDialog
				visible={showDeleteFeedbackDialog}
				handleDismiss={toggleShowDeleteFeedbackDialog}
				handleShowDeleteConfirmation={toggleShowDeleteConfirmationDialog}
				feedback={feedback}
				handleSetFeedback={handleSetFeedback}
				handleSelectOption={handleChangeCheckboxState}
				checkboxStates={checkboxStates}
				selectedReasons={selectedReasons}
			/>
			<DeleteConfirmationDialog
				visible={showDeleteConfirmationDialog}
				handleDismiss={toggleShowDeleteConfirmationDialog}
				handleSendFeedback={handleSendFeedback}
			/>
			<LanguageDialog
				visible={openDialog === "LANGUAGE"}
				handleClose={handleCloseDialog}
			/>
			<PurchaseDialog
				visible={openDialog === "PURCHASE"}
				handleClose={handleCloseDialog}
				productType="STUDY_PACK"
			/>
			<FaqsDialog
				visible={openDialog === "FAQS"}
				handleClose={handleCloseDialog}
			/>
			<FeedbackDialogContainer
				visible={isWeb && openDialog === "FEEDBACK"}
				onDismiss={handleCloseDialog}
			/>
			<AppSelectionDialog
				visible={openDialog === "APP_SELECTION"}
				handleClose={handleCloseDialog}
				currentApp={selectedApp || (buildType as Apps)}
			/>
		</>
	);
};

export default AccountSettings;
